import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';

import injectReducer from 'utils/injectReducer';
import injectSaga from 'utils/injectSaga';

import { logout } from 'containers/LoginPage/actions';

import { fetchMe, resetState } from './actions';
import authUtils from './authUtils';
import { subscriptionData } from 'utils/customer';
import { SUBSCRIPTION_STATES } from 'utils/constants';

import { makeSelectAuthenticated, makeSelectLoading, makeSelectUser, makeSelectError } from './selectors';
import reducer from './reducer';
import saga from './saga';

// Inject reducer and saga at module load time
const injectedReducer = injectReducer({ key: 'auth', reducer });
const injectedSaga = injectSaga({ key: 'auth', saga });

function AuthenticationContent(props) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const authenticated = useSelector(makeSelectAuthenticated());
  const loading = useSelector(makeSelectLoading());
  const user = useSelector(makeSelectUser());
  const error = useSelector(makeSelectError());

  const { requireAuth, component: ComposedComponent } = props;
  const prevErrorRef = useRef(error);

  useEffect(() => {
    if (!requireAuth) {
      if (authenticated && user && Object.keys(user).length > 0) {
        // Allow inactive users to access signup to complete their checkout
        const userData = subscriptionData(user);
        const { subscriptionState } = userData;
        if (subscriptionState !== SUBSCRIPTION_STATES['INACTIVE']) {
          navigate('/contract');
        }
      }
    } else if(!loading && (!user || Object.keys(user).length === 0)){
      dispatch(fetchMe());
    }

    if (user) {
      const {profile_data} = user;
      if(profile_data) {
        const {email, name} = profile_data;
        if(name && email) {
          window.$kundo_chat=window.$kundo_chat||{};
          window.$kundo_chat.user={
            'name': name,
            'email': email,
          };
        }
      }
    }
  }, [dispatch, navigate]);

  useEffect(() => {
    if (error && !prevErrorRef.current) {
      console.log('[WithAuth] redirect due to error', { error, authenticated, loading });
      prevErrorRef.current = true;
      dispatch(resetState());
      dispatch(logout());
      navigate('/login');
    } else if (requireAuth && !authenticated && !loading) {
      const token = authUtils.getAuthToken();
      if (!token) {
        console.log('[WithAuth] redirect due to missing token (unauthenticated)');
        dispatch(logout());
        navigate('/login');
      } else {
        console.log('[WithAuth] token present; waiting for fetchMe to resolve');
      }
    } else if (!requireAuth && authenticated && !loading) {
      // Allow inactive users to stay on public routes (like signup) to complete checkout
      const userData = subscriptionData(user);
      const { subscriptionState } = userData;
      if (subscriptionState !== SUBSCRIPTION_STATES['INACTIVE']) {
        console.log('[WithAuth] redirect to contract (already authenticated)');
        navigate('/contract');
      }
    }

    prevErrorRef.current = error;
  }, [error, requireAuth, authenticated, loading, navigate, dispatch]);

  // Create a history-like object for backward compatibility with React Router v5 API
  const history = {
    push: (path) => {
      navigate(path);
    },
    goBack: () => {
      navigate(-1);
    },
  };

  return <ComposedComponent {...props} navigate={navigate} history={history} location={location} />;
}

AuthenticationContent.propTypes = {
  component: PropTypes.elementType.isRequired,
  requireAuth: PropTypes.bool.isRequired,
};

// Create the wrapped component outside of WithAuth to avoid recreating on every render
const WrappedAuthenticationContent = injectedReducer(injectedSaga(AuthenticationContent));

// Wrapper component that injects auth reducer and saga
function WithAuth(props) {
  return <WrappedAuthenticationContent {...props} />;
}

export default WithAuth;